import { ComponentPropsWithoutRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { PLUS_MARKETING_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import DarkerGrainBg from 'images/gbp-grain-darker.png';
import GrainBg from 'images/gbp-grain.png';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import { twMerge } from 'tailwind-merge';

interface GivebutterSplashRootProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashRoot = (props: GivebutterSplashRootProps) => {
    const { children, className = '', ...restProps } = props;
    return (
        <div className={className} {...restProps}>
            {children}
        </div>
    );
};

interface GivebutterSplashContentProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashContent = (props: GivebutterSplashContentProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge('m-auto flex max-w-[60rem] flex-col gap-6 pt-12', className)}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface GivebutterSplashHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashHeader = (props: GivebutterSplashHeaderProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge(
                'flex flex-wrap items-center justify-between gap-8 pb-16 sm:flex-nowrap',
                className,
            )}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface GivebutterSplashTextProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashText = (props: GivebutterSplashTextProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div className={twMerge('flex w-[25.5rem] flex-col gap-8', className)} {...restProps}>
            {children}
        </div>
    );
};

interface GivebutterSplashTitleProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashTitle = (props: GivebutterSplashTitleProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge('text-[3.5rem] font-medium leading-[1.5rem]', className)}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface GivebutterSplashDescriptionProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashDescription = (props: GivebutterSplashDescriptionProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div className={twMerge('text-lg text-gray-700', className)} {...restProps}>
            {children}
        </div>
    );
};

interface GivebutterSplashCallToActionProps extends ComponentPropsWithoutRef<'div'> {
    learnMoreUrl?: string;
}
const GivebutterSplashCallToAction = (props: GivebutterSplashCallToActionProps) => {
    const { className = '', learnMoreUrl, ...restProps } = props;
    const { accountId } = useCurrentAccount();

    return (
        <div className={twMerge('flex items-center gap-2', className)} {...restProps}>
            <a href={learnMoreUrl ?? PLUS_MARKETING_LINK} target="_blank" rel="noreferrer">
                <Button variant="outline" color="neutral">
                    Learn more
                </Button>
            </a>
            <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                <Button variant="solid">Start your free trial</Button>
            </Link>
        </div>
    );
};

const GivebutterSplashHeaderLogo = () => {
    return (
        <div>
            <img src={GivebutterPlusLogo} className="w-48" alt="Givebutter Plus logo" />
        </div>
    );
};

interface GivebutterSplashHeaderImageProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashHeaderImage = (props: GivebutterSplashHeaderImageProps) => {
    const { className = '', ...restProps } = props;
    return (
        <div
            className={twMerge(
                'hidden max-w-[30rem] flex-shrink items-center justify-center rounded-3xl p-2 sm:flex md:p-10 [&>img]:w-full [&>img]:max-w-[30rem]',
                className,
            )}
            style={{
                background: `url("${DarkerGrainBg}") repeat`,
            }}
            {...restProps}
        />
    );
};

interface GivebutterSplashFeatureCardRowProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashFeatureCardRow = (props: GivebutterSplashFeatureCardRowProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge(
                'flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap',
                className,
            )}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface GivebutterSplashFeatureCardProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashFeatureCard = (props: GivebutterSplashFeatureCardProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge(
                'flex w-full flex-col gap-6 self-stretch rounded-xl bg-gray-100 p-6',
                className,
            )}
            style={{
                background: `url("${GrainBg}") repeat`,
            }}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface GivebutterSplashFeatureCardTextProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashFeatureCardText = (props: GivebutterSplashFeatureCardTextProps) => {
    const { children, className = '', ...restProps } = props;
    return (
        <div className={twMerge('flex flex-col gap-2', className)} {...restProps}>
            {children}
        </div>
    );
};

interface GivebutterSplashFeatureCardTextHeadingProps extends ComponentPropsWithoutRef<'p'> {}
const GivebutterSplashFeatureCardTextHeading = (
    props: GivebutterSplashFeatureCardTextHeadingProps,
) => {
    const { children, className = '', ...restProps } = props;
    return (
        <p className={twMerge('text-lg font-medium', className)} {...restProps}>
            {children}
        </p>
    );
};

interface GivebutterSplashFeatureCardTextDescriptionProps extends ComponentPropsWithoutRef<'p'> {}
const GivebutterSplashFeatureCardTextDescription = (
    props: GivebutterSplashFeatureCardTextDescriptionProps,
) => {
    const { children, className = '', ...restProps } = props;
    return (
        <p className={twMerge('text-gray-600', className)} {...restProps}>
            {children}
        </p>
    );
};

interface GivebutterSplashFeatureCardIconProps extends ComponentPropsWithoutRef<'div'> {}
const GivebutterSplashFeatureCardIcon = (props: GivebutterSplashFeatureCardIconProps) => {
    const { children, className = '', ...restProps } = props;
    return (
        <div
            className={twMerge('flex h-12 w-12 items-center justify-center rounded-lg', className)}
            {...restProps}
        >
            {children}
        </div>
    );
};

const GivebutterSplashFooter = () => {
    const { accountId } = useCurrentAccount();
    return (
        <div className="flex flex-col gap-4 py-16">
            <div className="flex flex-col gap-4">
                <div>
                    <img
                        src={GivebutterPlusLogo}
                        className="m-auto w-56"
                        alt="Givebutter Plus logo"
                    />
                </div>
                <div className="text-center text-lg text-gray-700">
                    Unlock the potential of your favorite fundraising tool.
                </div>
            </div>
            <div className="flex items-center justify-center gap-4">
                <a href={PLUS_MARKETING_LINK} target="_blank" rel="noreferrer">
                    <Button size="md" variant="outline" color="neutral">
                        Learn more
                    </Button>
                </a>
                <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                    <Button variant="solid" size="md">
                        Upgrade to Givebutter Plus
                    </Button>
                </Link>
            </div>
        </div>
    );
};

const Root = GivebutterSplashRoot;
const Content = GivebutterSplashContent;
const Header = GivebutterSplashHeader;
const Title = GivebutterSplashTitle;
const Description = GivebutterSplashDescription;
const CallToAction = GivebutterSplashCallToAction;
const HeaderLogo = GivebutterSplashHeaderLogo;
const HeaderImage = GivebutterSplashHeaderImage;
const Text = GivebutterSplashText;
const FeatureCardRow = GivebutterSplashFeatureCardRow;
const FeatureCard = GivebutterSplashFeatureCard;
const FeatureCardText = GivebutterSplashFeatureCardText;
const FeatureCardIcon = GivebutterSplashFeatureCardIcon;
const FeatureCardTextHeading = GivebutterSplashFeatureCardTextHeading;
const FeatureCardTextDescription = GivebutterSplashFeatureCardTextDescription;
const Footer = GivebutterSplashFooter;

export {
    GivebutterSplashRoot,
    GivebutterSplashContent,
    GivebutterSplashHeader,
    GivebutterSplashTitle,
    GivebutterSplashDescription,
    GivebutterSplashCallToAction,
    GivebutterSplashHeaderLogo,
    GivebutterSplashHeaderImage,
    GivebutterSplashText,
    GivebutterSplashFeatureCardRow,
    GivebutterSplashFeatureCard,
    GivebutterSplashFeatureCardIcon,
    GivebutterSplashFeatureCardText,
    GivebutterSplashFeatureCardTextHeading,
    GivebutterSplashFeatureCardTextDescription,
    GivebutterSplashFooter,
    //
    Root,
    Content,
    Header,
    Title,
    Description,
    CallToAction,
    HeaderLogo,
    HeaderImage,
    Text,
    FeatureCardRow,
    FeatureCard,
    FeatureCardIcon,
    FeatureCardText,
    FeatureCardTextHeading,
    FeatureCardTextDescription,
    Footer,
};
